import React, { useState, useCallback, useEffect } from 'react';
import './App.css';

function App() {
  const [arts, setArts] = useState([]);

  const createSeededRandom = (seed) => {
    let x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  };

  const artStyles = [
    {
      name: 'Fractal Dreamweaver',
      generateArt: (ctx, width, height, seed) => {
        const seededRandom = (max = 1) => createSeededRandom(seed) * max;
        const seededRandomInt = (min, max) => 
          Math.floor(seededRandom() * (max - min + 1)) + min;

        const drawFractal = (x, y, size, depth, angle) => {
          if (depth === 0) return;

          const hue = (depth * 50 + seed) % 360;
          const saturation = 50 + depth * 10;
          const lightness = 30 + depth * 5;
          
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(angle);

          const fractalType = seededRandom();
          
          if (fractalType < 0.3) {
            ctx.strokeStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${0.7 - depth * 0.1})`;
            ctx.lineWidth = Math.max(1, 5 - depth);
            
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(0, -size);
            ctx.stroke();

            if (depth > 0) {
              drawFractal(0, -size, size * 0.7, depth - 1, Math.PI / 4);
              drawFractal(0, -size, size * 0.7, depth - 1, -Math.PI / 4);
            }
          } else if (fractalType < 0.6) {
            const drawKochCurve = (x1, y1, x2, y2, depth) => {
              if (depth === 0) {
                ctx.lineTo(x2, y2);
                return;
              }

              const deltaX = x2 - x1;
              const deltaY = y2 - y1;
              
              const x3 = x1 + deltaX / 3;
              const y3 = y1 + deltaY / 3;
              
              const x4 = x1 + deltaX * 0.5 + Math.sqrt(3) / 6 * (y2 - y1);
              const y4 = y1 + deltaY * 0.5 - Math.sqrt(3) / 6 * (x2 - x1);
              
              const x5 = x1 + deltaX * 2 / 3;
              const y5 = y1 + deltaY * 2 / 3;

              drawKochCurve(x1, y1, x3, y3, depth - 1);
              drawKochCurve(x3, y3, x4, y4, depth - 1);
              drawKochCurve(x4, y4, x5, y5, depth - 1);
              drawKochCurve(x5, y5, x2, y2, depth - 1);
            };

            ctx.strokeStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${0.7 - depth * 0.1})`;
            ctx.lineWidth = Math.max(1, 5 - depth);
            
            ctx.beginPath();
            ctx.moveTo(-size/2, size/2);
            drawKochCurve(-size/2, size/2, size/2, size/2, depth);
            drawKochCurve(size/2, size/2, 0, -size/2, depth);
            drawKochCurve(0, -size/2, -size/2, size/2, depth);
            ctx.closePath();
            ctx.stroke();
          } else {
            const drawSierpinskiTriangle = (x1, y1, x2, y2, x3, y3, depth) => {
              if (depth === 0) {
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.lineTo(x3, y3);
                ctx.closePath();
                ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${0.7 - depth * 0.1})`;
                ctx.fill();
                return;
              }

              const x12 = (x1 + x2) / 2;
              const y12 = (y1 + y2) / 2;
              const x23 = (x2 + x3) / 2;
              const y23 = (y2 + y3) / 2;
              const x31 = (x3 + x1) / 2;
              const y31 = (y3 + y1) / 2;

              drawSierpinskiTriangle(x1, y1, x12, y12, x31, y31, depth - 1);
              drawSierpinskiTriangle(x12, y12, x2, y2, x23, y23, depth - 1);
              drawSierpinskiTriangle(x31, y31, x23, y23, x3, y3, depth - 1);
            };

            drawSierpinskiTriangle(-size/2, size/2, size/2, size/2, 0, -size/2, depth);
          }

          ctx.restore();
        };

        ctx.fillStyle = `hsl(${seededRandom(360)}, 20%, 10%)`;
        ctx.fillRect(0, 0, width, height);

        const fractalCount = seededRandomInt(3, 7);
        for (let i = 0; i < fractalCount; i++) {
          drawFractal(
            seededRandom() * width, 
            seededRandom() * height, 
            seededRandom() * 100, 
            seededRandomInt(3, 6), 
            seededRandom() * Math.PI * 2
          );
        }
      }
    },
    {
      name: 'Abstract Chaos', 
      generateArt: (ctx, width, height, seed) => {
        const seededRandom = (max = 1) => createSeededRandom(seed) * max;
        const seededRandomInt = (min, max) => 
          Math.floor(seededRandom() * (max - min + 1)) + min;

        for (let i = 0; i < 100; i++) {
          ctx.beginPath();
          ctx.fillStyle = `hsl(${seededRandom(360)}, 70%, 60%)`;
          
          const shapeType = seededRandom();
          if (shapeType < 0.3) {
            ctx.ellipse(
              seededRandom() * width, 
              seededRandom() * height, 
              seededRandom() * 100, 
              seededRandom() * 50, 
              seededRandom() * Math.PI, 
              0, 
              2 * Math.PI
            );
          } else if (shapeType < 0.6) {
            const sides = seededRandomInt(3, 8);
            const centerX = seededRandom() * width;
            const centerY = seededRandom() * height;
            const radius = seededRandom() * 80;
            
            ctx.moveTo(
              centerX + radius * Math.cos(0),
              centerY + radius * Math.sin(0)
            );
            
            for (let j = 1; j <= sides; j++) {
              const angle = j * 2 * Math.PI / sides;
              ctx.lineTo(
                centerX + radius * Math.cos(angle),
                centerY + radius * Math.sin(angle)
              );
            }
          } else {
            ctx.moveTo(seededRandom() * width, seededRandom() * height);
            ctx.bezierCurveTo(
              seededRandom() * width, 
              seededRandom() * height,
              seededRandom() * width, 
              seededRandom() * height,
              seededRandom() * width, 
              seededRandom() * height
            );
          }
          
          ctx.fill();
        }
      }
    },
    {
      name: 'Digital Dreamscape',
      generateArt: (ctx, width, height, seed) => {
        const seededRandom = (max = 1) => createSeededRandom(seed) * max;
        const seededRandomInt = (min, max) => 
          Math.floor(seededRandom() * (max - min + 1)) + min;

        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, width, height);

        const mainStyle = seededRandomInt(1, 6);
        const layerCount = seededRandomInt(3, 7);
        
        // Reduzindo drasticamente a área de desenho
        const safeMarginTop = 100;
        const safeMarginBottom = 120;
        const safeMarginSides = 100;
        const safeWidth = Math.floor(width * 0.6);
        const safeHeight = Math.floor(height * 0.6);

        for (let layer = 0; layer < layerCount; layer++) {
          ctx.save();
          
          // Centralizar e limitar a área de desenho
          // Centralizando o canvas na área disponível
          ctx.translate(
            (width - safeWidth) / 2,
            (height - safeHeight) / 2
          );
          ctx.beginPath();
          ctx.rect(0, 0, safeWidth, safeHeight);
          ctx.clip();

          switch(mainStyle) {
            case 1:
              ctx.fillStyle = `hsla(${seededRandom(360)}, 100%, 50%, ${seededRandom(0.9)})`;
              ctx.fillRect(
                seededRandom() * safeWidth, 
                seededRandom() * safeHeight, 
                seededRandom() * (safeWidth / 2), 
                seededRandom() * (safeHeight / 2)
              );
              break;

            case 2:
              ctx.strokeStyle = `hsla(${seededRandom(360)}, 100%, 70%, ${seededRandom(0.9)})`;
              ctx.lineWidth = seededRandom(20);
              ctx.moveTo(0, seededRandom() * safeHeight);
              ctx.lineTo(safeWidth, seededRandom() * safeHeight);
              ctx.stroke();
              break;

            case 3:
              const centerX = seededRandom() * safeWidth;
              const centerY = seededRandom() * safeHeight;
              const radius = seededRandom() * (Math.min(safeWidth, safeHeight) / 3);

              const gradient = ctx.createRadialGradient(
                centerX, centerY, 0,
                centerX, centerY, radius
              );
              const hue = seededRandom(360);
              gradient.addColorStop(0, `hsla(${hue}, 100%, 70%, ${seededRandom(0.9)})`);
              gradient.addColorStop(0.5, `hsla(${hue + 30}, 100%, 60%, ${seededRandom(0.7)})`);
              gradient.addColorStop(1, `hsla(${hue + 60}, 100%, 50%, 0)`);
              ctx.fillStyle = gradient;
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
              ctx.fill();

              for (let i = 0; i < 10; i++) {
                ctx.beginPath();
                ctx.fillStyle = `hsla(${hue}, 100%, 80%, ${seededRandom(0.7)})`;
                ctx.arc(
                  centerX + seededRandom(-radius/2, radius/2),
                  centerY + seededRandom(-radius/2, radius/2),
                  seededRandom(3, 8),
                  0,
                  Math.PI * 2
                );
                ctx.fill();
              }
              break;

            case 4:
              ctx.fillStyle = `hsla(${seededRandom(360)}, 100%, 60%, ${seededRandom(0.8)})`;
              ctx.beginPath();
              ctx.moveTo(seededRandom() * width, seededRandom() * height);
              ctx.lineTo(seededRandom() * width, seededRandom() * height);
              ctx.lineTo(seededRandom() * width, seededRandom() * height);
              ctx.closePath();
              ctx.fill();
              break;

            case 5:
              const pixelCenterX = seededRandom() * width;
              const pixelCenterY = seededRandom() * height;
              const pixelCount = seededRandomInt(20, 50);
              const pixelHue = seededRandom(360);
              
              for (let i = 0; i < pixelCount; i++) {
                const distance = seededRandom() * (Math.min(safeWidth, safeHeight) / 4);
                const angle = seededRandom() * Math.PI * 2;
                const x = Math.min(Math.max(0, pixelCenterX + Math.cos(angle) * distance), safeWidth);
                const y = Math.min(Math.max(0, pixelCenterY + Math.sin(angle) * distance), safeHeight);
                
                ctx.fillStyle = `hsla(${pixelHue}, 100%, 70%, ${seededRandom(0.8)})`;
                ctx.fillRect(
                  x,
                  y,
                  seededRandom(2, 6),
                  seededRandom(2, 6)
                );
              }
              break;

            case 6:
              const hexSize = seededRandom() * (Math.min(safeWidth, safeHeight) / 8) + 10;
              const hexCount = seededRandomInt(3, 8);
              const hexBaseHue = seededRandom(360);
              
              for (let i = 0; i < hexCount; i++) {
                const hexCenterX = seededRandom() * width;
                const hexCenterY = seededRandom() * height;
                
                ctx.beginPath();
                for (let j = 0; j < 6; j++) {
                  const angle = j * Math.PI / 3;
                  const x = hexCenterX + hexSize * Math.cos(angle);
                  const y = hexCenterY + hexSize * Math.sin(angle);
                  if (j === 0) ctx.moveTo(x, y);
                  else ctx.lineTo(x, y);
                }
                ctx.closePath();
                
                const hexGradient = ctx.createRadialGradient(
                  hexCenterX, hexCenterY, 0,
                  hexCenterX, hexCenterY, hexSize
                );
                hexGradient.addColorStop(0, `hsla(${hexBaseHue + i * 30}, 100%, 70%, ${seededRandom(0.7)})`);
                hexGradient.addColorStop(1, `hsla(${hexBaseHue + i * 30}, 100%, 50%, 0)`);
                
                ctx.fillStyle = hexGradient;
                ctx.fill();
                
                if (i > 0) {
                  ctx.beginPath();
                  ctx.strokeStyle = `hsla(${hexBaseHue + i * 30}, 100%, 70%, ${seededRandom(0.5)})`;
                  ctx.lineWidth = seededRandom() * 3;
                  ctx.moveTo(hexCenterX, hexCenterY);
                  const maxOffset = Math.min(safeWidth, safeHeight) / 6;
                  ctx.lineTo(
                    Math.min(Math.max(0, hexCenterX + seededRandom(-maxOffset, maxOffset)), safeWidth),
                    Math.min(Math.max(0, hexCenterY + seededRandom(-maxOffset, maxOffset)), safeHeight)
                  );
                  ctx.stroke();
                }
              }
              break;
          }
          
          ctx.restore();

        }
      }
    }
  ];

  const generateArt = (style, seed) => {
    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    style.generateArt(ctx, canvas.width, canvas.height, seed);

    return {
      image: canvas.toDataURL(),
      artist: style.name
    };
  };

  const handleGenerateArt = useCallback(() => {
    const allArts = artStyles.flatMap(style => 
      Array.from({length: 3}, (_, i) => 
        generateArt(style, Date.now() + i)
      )
    );
    setArts(allArts);
  }, []);

  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [generationInterval, setGenerationInterval] = useState(null);

  const handleKeyDown = (event) => {
    if (event.code === 'Space' && !isSpacePressed) {
      setIsSpacePressed(true);
      handleGenerateArt();
      const interval = setInterval(handleGenerateArt, 100); // Gera arte a cada 100ms
      setGenerationInterval(interval);
    }
  };

  const handleKeyUp = (event) => {
    if (event.code === 'Space') {
      setIsSpacePressed(false);
      if (generationInterval) {
        clearInterval(generationInterval);
        setGenerationInterval(null);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (generationInterval) {
        clearInterval(generationInterval);
      }
    };
  }, [handleGenerateArt, isSpacePressed, generationInterval]);

  useEffect(() => {
    handleGenerateArt();
  }, [handleGenerateArt]);

  return (
    <div className="App">
      <div className="art-grid">
        {arts.map((art, index) => (
          <div key={index} className="art-piece">
            <img src={art.image} alt={`Art ${index + 1}`} />
            <div className="artist-name">{art.artist}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
